import { FC } from 'react';
import classNames from 'classnames';
import { ModalComponent } from 'components/Modal';

const SHARE_TEXT_CHAR_LIMIT = 280;

interface SharePlatform {
  label: string;
  value: 'x_twitter' | 'facebook' | 'instagram';
  url: string;
}

const getSharePlatforms = (trackedUrl: string): SharePlatform[] => [
  { label: 'X / Twitter', value: 'x_twitter', url: 'https://twitter.com/intent/tweet' },
  {
    label: 'Facebook',
    value: 'facebook',
    url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(trackedUrl)}`,
  },
  { label: 'Instagram', value: 'instagram', url: 'https://www.instagram.com/' },
];

interface ShareScorePopupProps {
  isOpen: boolean;
  onClose: () => void;
  shareText: string;
  trackedUrl: string;
  onCopy: () => void;
  onPlatformSelect: (platform: SharePlatform['value']) => void;
}

export const ShareScorePopup: FC<ShareScorePopupProps> = ({
  isOpen,
  onClose,
  shareText,
  trackedUrl,
  onCopy,
  onPlatformSelect,
}) => {
  const isOverLimit = shareText.length > SHARE_TEXT_CHAR_LIMIT;

  const handleCopyAndClose = () => {
    onCopy();
    onClose();
  };

  const handlePlatformClick = (platform: SharePlatform) => {
    onPlatformSelect(platform.value);
    onClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      closeModal={onClose}
      contentClassName="badge-popup"
      contentLabel="Share your score"
    >
      <span className="badge-popup__title">Share your score</span>
      <span className="badge-popup__description">
        Copy this text, then post it directly to Instagram, X, or Facebook.
      </span>
      <div className="badge-popup__share-text">
        <p>{shareText}</p>
      </div>
      <span
        className={classNames('badge-popup__char-count', {
          'badge-popup__char-count--over': isOverLimit,
        })}
      >
        {shareText.length} / {SHARE_TEXT_CHAR_LIMIT} characters
      </span>
      <span className="badge-popup__link-note">{`Link included: ${trackedUrl}`}</span>
      <div className="badge-popup__platforms">
        {getSharePlatforms(trackedUrl).map(platform => (
          <a
            key={platform.value}
            href={platform.url}
            target="_blank"
            rel="noopener noreferrer"
            className="badge-popup__platform-link body-small-text"
            onClick={() => handlePlatformClick(platform)}
          >
            {platform.label}
          </a>
        ))}
      </div>
      <div className="badge-popup__actions">
        <button type="button" className="btn-inkBlue-inverted btn btn-secondary" onClick={onClose}>
          <span className="body-small-text">Close</span>
        </button>
        <button
          type="button"
          className="btn-oathBlue btn btn-secondary"
          onClick={handleCopyAndClose}
        >
          <span className="body-small-text text-white">Copy Text</span>
        </button>
      </div>
    </ModalComponent>
  );
};
